import { useState, useCallback } from "react";
import axios from "axios";
import api from "../../services/api";
import { queryService } from "../../services/queryService";
import { useSnackbar } from "../../components/common/Snackbar";
import { useAuth } from "../../context/AuthContext";
import { WebConfig } from "../../web-config";

/**
 * Hook to manage query assignment/delegation state and logic
 * @param {function} onQueryUpdate - Callback when query is updated
 * @param {function} onTimelineFetch - Callback to fetch timeline
 * @returns {Object} Assignment state and handlers
 */
export const useQueryAssignment = (
  onQueryUpdate,
  onTimelineFetch,
  onDashboardRefresh,
  projectsCatalog = []
) => {
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignAllUsers, setAssignAllUsers] = useState([]);
  const [assignRegion, setAssignRegion] = useState("");
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const [loadingAssignUsers, setLoadingAssignUsers] = useState(false);
  const [assignReason, setAssignReason] = useState("Handing off to responder");
  const [assignNote, setAssignNote] = useState("Awaiting response");

  const loadAssignableUsers = useCallback(
    async (query, regionValue) => {
      setLoadingAssignUsers(true);
      try {
        const storedUser = user || JSON.parse(localStorage.getItem("user"));

        if (!storedUser) {
          showSnackbar("User information not found", "error");
          setAssignUsers([]);
          setAssignRegion("");
          return;
        }

        // Resolve project code from catalog using query's projectName
        const catalog = Array.isArray(projectsCatalog) ? projectsCatalog : [];
        const projectName = query?.projectName || "";
        const matchedProject = catalog.find(
          (p) =>
            (p.description || "").toLowerCase() === projectName.toLowerCase() ||
            (p.name || "").toLowerCase() === projectName.toLowerCase()
        );
        const projectCode = matchedProject?.code || "";

        if (!projectCode) {
          showSnackbar("Project not found for this query", "error");
          setAssignUsers([]);
          setAssignRegion(regionValue || "");
          return;
        }

        const response = await axios.get(
          `${WebConfig.REACT_APP_AAI_SSO_BASE_URL}/SEPL.PWIMS.AppServices/ProjectTarget/ProjectTarget.svc/GetProjectAssignee`,
          {
            params: {
              ProjectCode: projectCode,
              UserName: storedUser?.name,
              orgId: 2
            },
            withCredentials: false
          }
        );

        const apiData = response?.data?.data || [];
        const projectUsers = apiData.flatMap((group) =>
          (group.Value || []).map((u) => ({ ...u, role: group.Key }))
        );

        // Exclude the current assignee from the list
        const currentAssigneeId = query?.assignee?._id?.toString() || "";
        const filtered = projectUsers
          .filter((u) => {
            if (!currentAssigneeId) return true;
            return u._id?.toString() !== currentAssigneeId;
          })
          .sort((a, b) =>
            (a.full_name || a.name || "").localeCompare(
              b.full_name || b.name || "",
              undefined,
              { sensitivity: "base" }
            )
          );

        setAssignAllUsers(filtered);
        setAssignUsers(filtered);
        setAssignRegion(regionValue || "");
      } catch (error) {
        showSnackbar("Failed to load users", "error");
        setAssignUsers([]);
        setAssignRegion(regionValue || "");
      } finally {
        setLoadingAssignUsers(false);
      }
    },
    [user, showSnackbar, projectsCatalog]
  );

  const handleOpenAssignDialog = useCallback(
    (query) => {
      setAssignTarget(query);
      setAssignDialogOpen(true);
      setSelectedAssigneeId("");
      setAssignReason("Handing off to responder");
      setAssignNote("Awaiting response");
      loadAssignableUsers(query, query?.region);
    },
    [loadAssignableUsers]
  );

  const handleCloseAssignDialog = useCallback(() => {
    setAssignDialogOpen(false);
    setAssignTarget(null);
    setAssignUsers([]);
    setAssignAllUsers([]);
    setAssignRegion("");
    setSelectedAssigneeId("");
    setAssigningId(null);
    setAssignReason("Handing off to responder");
    setAssignNote("Awaiting response");
  }, []);

  const handleConfirmAssign = useCallback(async () => {
    if (!assignTarget?._id) return;
    if (!selectedAssigneeId) {
      showSnackbar("Please select an assignee", "error");
      return;
    }

    const queryId = assignTarget._id;
    setAssigningId(queryId);
    try {
      // 1️⃣ POST /api/queries/:queryId/reassign — delegation action
      await api.post(`/queries/${queryId}/reassign`, {
        action: "assign",
        assigneeId: selectedAssigneeId,
        reason: assignReason?.trim() || "Handing off to responder",
        status: "delegated",
        statusLabel: "delegated",
        statusResponse: assignNote?.trim() || "Awaiting response"
      });

      // 2️⃣ GET /api/queries/:queryId — reload latest query state
      let updatedQuery = null;
      try {
        updatedQuery = await queryService.getQueryById(queryId);
      } catch (fetchErr) {
        // console.warn('Could not reload query after delegation:', fetchErr);
      }

      showSnackbar("Query delegated successfully", "success");
      handleCloseAssignDialog();

      // 3️⃣ Push fresh query data to the modal so it re-renders immediately
      if (onQueryUpdate && updatedQuery) {
        onQueryUpdate(updatedQuery);
      }

      // 4️⃣ Refresh the activity timeline silently
      if (onTimelineFetch) {
        onTimelineFetch({ silent: true });
      }

      // 5️⃣ Notify dashboard to refresh its table (optional callback)
      if (onDashboardRefresh) {
        onDashboardRefresh();
      }
    } catch (error) {
      // console.error('Failed to assign query:', error);
      const message = error.response?.data?.message || "Failed to assign query";
      showSnackbar(message, "error");
      setAssigningId(null);
    }
  }, [
    assignTarget,
    selectedAssigneeId,
    assignReason,
    assignNote,
    handleCloseAssignDialog,
    showSnackbar,
    onQueryUpdate,
    onTimelineFetch,
    onDashboardRefresh
  ]);

  const handleAssignRegionChange = useCallback((event) => {
    setAssignRegion(event.target.value);
  }, []);

  return {
    assignDialogOpen,
    assignTarget,
    assigningId,
    assignUsers,
    assignAllUsers,
    assignRegion,
    selectedAssigneeId,
    setSelectedAssigneeId,
    loadingAssignUsers,
    assignReason,
    setAssignReason,
    assignNote,
    setAssignNote,
    handleOpenAssignDialog,
    handleCloseAssignDialog,
    handleConfirmAssign,
    handleAssignRegionChange,
    loadAssignableUsers
  };
};
